import React, { useEffect, useState } from 'react';
import * as S from './AiConversationBanner.styles';
import { useDispatch, useSelector } from 'react-redux';
import { useAiChat } from '@/hooks/AiChat';
import { AiChatForm, MiniCard, Header } from './components';
import { merlinActions } from '@/redux/modules';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import { ATIVACAO_AICHAT_AUDIO_RECORDING, ATIVACAO_N8N_INHOUSE_URLS } from '@/config/GrowthBook/constants';
import { WSS_ROUTES } from '@/components/Layout/Menu/HeaderV2/AiChat/AiChat.enum';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { useResize } from '@/hooks/useResize';

const AiConversationBanner = ({ locale }) => {
  const {
    betaTag, title, suggestions, input,
  } = locale;

  const dispatch = useDispatch();
  const { triggerExternalMessage, triggerExternalAudioBlob } = useAiChat();
  const { recording, handleStartRecording, handleStopRecording } = useAudioRecorder({ onStop: triggerExternalAudioBlob });
  const { firstname } = useSelector(state => state.summary);
  const { currentChat, loadingTranscription } = useSelector(state => state.merlin);
  const [messageContent, setMessageContent] = useState('');
  const [renderedSuggestions, setRenderedSuggestions] = useState([]);
  const [interactedWithSuggestions, setInteractedWithSuggestions] = useState(false);
  const windowRect = useResize();
  const isMobile = windowRect && windowRect.width <= 960;

  const isAudioRecordingFeatureActive = useFeatureIsOn(ATIVACAO_AICHAT_AUDIO_RECORDING);
  const wssToggleRoutes = useFeatureValue(ATIVACAO_N8N_INHOUSE_URLS);

  const chatIsOpen = Boolean(currentChat.threadid);
  const isRecording = recording || loadingTranscription;

  useEffect(() => {
    if (isMobile) {
      setRenderedSuggestions(suggestions.filter(suggestion => suggestion.action !== 'expand'));
      return;
    }
    setRenderedSuggestions(suggestions.filter(suggestion => !suggestion.hideOnInit));
  }, [suggestions, isMobile]);

  const handleExpandSuggestions = () => {
    setRenderedSuggestions(suggestions.filter(suggestion => suggestion.action !== 'expand'));
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();

    if (chatIsOpen || !messageContent) {
      return;
    }

    triggerExternalMessage({ message: messageContent.trim() });
    setMessageContent('');
  };

  const handleEnter = (event) => {
    if (event.key === 'Enter') {
      handleFormSubmit(event);
    }
  };

  const handleSelectSuggestion = (action, initialMessage) => {
    if (action === 'expand') {
      handleExpandSuggestions();
      return;
    }

    const setRoute = (route) => {
      dispatch(merlinActions.inhouse.setRoute(route));
    };

    const routes = {
      email: wssToggleRoutes.email || WSS_ROUTES.EMAIL,
      domainConfig: wssToggleRoutes.domain || WSS_ROUTES.DOMAIN,
      newSite: wssToggleRoutes.site || WSS_ROUTES.SITE,
      general: wssToggleRoutes.general || WSS_ROUTES.GENERAL,
    };

    setRoute(routes[action] || routes.general);
    triggerExternalMessage({ message: initialMessage });
  };

  const handleScrollSuggestions = () => {
    setInteractedWithSuggestions(true);
  };

  const renderSuggestions = ({ noWrap = false } = {}) => renderedSuggestions.map(suggestion => (
    <MiniCard
      key={suggestion.id}
      Icon={suggestion.Icon}
      label={suggestion.label}
      onClick={() => handleSelectSuggestion(suggestion.action, suggestion.initialMessage)}
      disabled={chatIsOpen}
      noWrap={noWrap}
    />
  ));

  return (
    <S.Wrapper data-testid="ai-conversation-banner">
      <Header betaTag={betaTag} title={title} firstname={firstname} isRecording={isRecording} />
      <S.ConversationBox>
        <S.AiChatFormAdapter>
          <AiChatForm
            placeholder={chatIsOpen ? input.placeholderAlreadyOpened : input.placeholder}
            value={messageContent}
            onChange={e => setMessageContent(e.target.value)}
            onKeyDown={handleEnter}
            isRecording={isRecording}
            isAudioRecordingFeatureActive={isAudioRecordingFeatureActive}
            handleStartRecording={handleStartRecording}
            onSubmit={handleFormSubmit}
            chatIsOpen={chatIsOpen}
            handleStopRecording={handleStopRecording}
            recording={recording}
            loadingTranscription={loadingTranscription}
            audioLocale={locale.audio}
            disabled={chatIsOpen || isRecording}
          />
        </S.AiChatFormAdapter>
        <S.SuggestionsWrapper data-testid="suggestions-wrapper" data-interacted={interactedWithSuggestions} $interacted={interactedWithSuggestions} onScroll={handleScrollSuggestions}>
          {renderSuggestions({ noWrap: true })}
        </S.SuggestionsWrapper>
      </S.ConversationBox>
    </S.Wrapper>
  );
};

export default AiConversationBanner;
